import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import audioSpeakerIcon from '../../assets/icons/audio-speaker.png';
import {
  selectNeutralVoice,
  getSpeechLangCode,
  resolveContentSpeechLang,
  splitIntoSpeechChunks,
} from '../../utils/ttsVoiceHelper';

/**
 * True only when the full Web Speech synthesis API exists. Some in-app WebViews (e.g. WeChat / X5 / UC)
 * expose `speechSynthesis` without `SpeechSynthesisUtterance`, which would otherwise throw on first click.
 */
const isSpeechSupported = () =>
  typeof window !== 'undefined' &&
  'speechSynthesis' in window &&
  typeof window.SpeechSynthesisUtterance !== 'undefined';

// Voice reliability knowledge is shared across every article page for the lifetime of the tab, so a voice
// that is blocked/broken on this device (e.g. Chrome's Google voices behind the GFW) is not re-tried, and
// re-waited-for, on every article the user opens.
const sharedFailedVoiceURIs = new Set();
const sharedVerifiedVoiceURIs = new Set();

// Real HTML tags only (known tag name followed by whitespace, "/" or ">", and attributes must contain "=").
// Article text is rendered as plain text by the site, and authors use angle brackets as decoration, e.g.
// "<Davines CCA>" or "<< Creative colorist of the year >>". Those must be READ, not stripped as if they were tags.
const KNOWN_HTML_TAG =
  /<\/?(?:p|br|hr|div|span|b|i|u|s|em|strong|a|img|ul|ol|li|h[1-6]|table|thead|tbody|tr|td|th|font|section|article|blockquote|sup|sub)(?:\s[^<>]*=[^<>]*)?\s*\/?>/gi;

/**
 * Cleans real HTML tags, HTML entities, leftover angle brackets (kept as spaces so the words inside
 * "<...>" are still spoken), URLs (noisy when read aloud), and zero-width characters from a text fragment.
 */
const cleanReadableText = (raw = '') => {
  if (!raw) return '';
  return String(raw)
    .replace(KNOWN_HTML_TAG, ' ')
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, ' ')
    .replace(/&gt;/gi, ' ')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[<>]/g, ' ')
    .replace(/[-=_*~]{3,}/g, ' ')
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// Layout blocks whose `text` is part of the readable article body.
const READABLE_LAYOUT_TAGS = new Set([
  'content',
  'bullet',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'text',
  'p',
  'link',
]);

/**
 * Recursively collects readable text from layout blocks. Accordions can contain other accordions
 * (see the backend layout schema), so descend into them at any depth. Images, videos, buttons and ads
 * are not read aloud.
 */
const collectLayoutText = (items, out) => {
  if (!Array.isArray(items)) return;
  items.forEach((item) => {
    if (!item) return;
    const tag = String(item.tag || '').toLowerCase();
    if (READABLE_LAYOUT_TAGS.has(tag)) {
      const clean = cleanReadableText(item.text);
      if (clean) out.push(clean);
    } else if (tag === 'accordion') {
      const header = cleanReadableText(item.text);
      if (header) out.push(header);
      collectLayoutText(item.accordion, out);
    }
  });
};

/**
 * Extracts all plain-text readable content from an article's title, subtitle, layout blocks, and fallback content.
 * Formats sentences with clean delimiters for natural, uninterrupted speech synthesis.
 */
export const extractArticleFullText = (title = '', subtitle = '', layouts = [], content = '') => {
  const parts = [];
  const cleanTitle = cleanReadableText(title);
  const cleanSubtitle = cleanReadableText(subtitle);

  if (cleanTitle) parts.push(cleanTitle);
  if (cleanSubtitle) parts.push(cleanSubtitle);

  const layoutParts = [];
  collectLayoutText(layouts, layoutParts);

  if (layoutParts.length > 0) {
    parts.push(...layoutParts);
  } else if (content) {
    const cleanFallbackContent = cleanReadableText(content);
    if (cleanFallbackContent) parts.push(cleanFallbackContent);
  }

  return parts
    .map((p) => {
      const trimmed = p.trim();
      if (/[。！？.!?]$/.test(trimmed)) {
        return trimmed;
      }
      return `${trimmed}。`;
    })
    .join('\n');
};

/**
 * Compact Article Audio Player Button.
 * Uses a single, neutral, natural voice across devices without boy/girl/child artifacts.
 * Automatically adapts when the site language is switched, self-heals if a remote voice fails,
 * and ensures full article content is read aloud across mobile, tablet, and all browsers.
 */
export default function ArticleAudioPlayer({
  title = '',
  subtitle = '',
  layouts = [],
  content = '',
  className = '',
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [voices, setVoices] = useState(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        return window.speechSynthesis.getVoices() || [];
      } catch {
        return [];
      }
    }
    return [];
  });

  // Track speech status: 'stopped' | 'started' | 'paused'
  const [speechStatus, setSpeechStatus] = useState('stopped');
  const currentSentenceIdxRef = useRef(0);
  const lastCharIndexRef = useRef(0);
  const isPlayingRef = useRef(false);
  const sentencesRef = useRef([]);
  const voicesRef = useRef(voices);
  voicesRef.current = voices;

  // Hold strong reference to active SpeechSynthesisUtterance to prevent V8/WebKit GC drops
  const activeUtteranceRef = useRef(null);
  // Track playback generation ID so stale cancel/end events never corrupt active playback
  const playbackSessionRef = useRef(0);
  // Track voiceURIs that failed at runtime (e.g. unreachable Google/cloud TTS) for instant fallback
  const failedVoicesRef = useRef(sharedFailedVoiceURIs);
  // Track voiceURIs that have already proven to work so transient pause/cancel glitches never blacklist them
  const verifiedVoicesRef = useRef(sharedVerifiedVoiceURIs);
  // True once any utterance has actually started on this device (works even when using the default voice)
  const hasSpokenRef = useRef(false);
  // True once the CURRENT utterance has really started speaking (used to decide how to resume after pause)
  const utteranceStartedRef = useRef(false);
  const retryTimerRef = useRef(null);
  const watchdogTimerRef = useRef(null);
  const keepAliveIntervalRef = useRef(null);
  // End-of-utterance guard: recovers when a browser never fires `onend` (lost event / hung engine)
  const endGuardTimerRef = useRef(null);
  const rearmEndGuardRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
    if (watchdogTimerRef.current) {
      clearTimeout(watchdogTimerRef.current);
      watchdogTimerRef.current = null;
    }
    if (endGuardTimerRef.current) {
      clearTimeout(endGuardTimerRef.current);
      endGuardTimerRef.current = null;
    }
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
    }
  }, []);

  const startKeepAlive = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }
    keepAliveIntervalRef.current = setInterval(() => {
      if (!isPlayingRef.current || typeof window === 'undefined' || !window.speechSynthesis) {
        return;
      }
      try {
        if (window.speechSynthesis.speaking && window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
      } catch {}
    }, 5000);
  }, []);

  // Load and listen for available voices across browsers
  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const updateVoices = () => {
      try {
        const available = window.speechSynthesis.getVoices();
        if (Array.isArray(available) && available.length > 0) {
          voicesRef.current = available;
          setVoices(available);
        }
      } catch {}
    };

    updateVoices();
    try {
      window.speechSynthesis.addEventListener('voiceschanged', updateVoices);
    } catch {
      window.speechSynthesis.onvoiceschanged = updateVoices;
    }
    return () => {
      try {
        window.speechSynthesis.removeEventListener('voiceschanged', updateVoices);
      } catch {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, []);

  const fullText = useMemo(() => {
    return extractArticleFullText(title, subtitle, layouts, content);
  }, [title, subtitle, layouts, content]);

  // Resolve speech language based on content and site language.
  const speechLang = useMemo(() => {
    const siteLang = getSpeechLangCode(i18n.language);
    return resolveContentSpeechLang(fullText, siteLang);
  }, [fullText, i18n.language]);

  const speechLangRef = useRef(speechLang);
  speechLangRef.current = speechLang;

  // Split article into short chunks (sentences, then clauses) for maximum speech synthesis reliability
  const sentences = useMemo(() => {
    if (!fullText) return [];
    const chunks = splitIntoSpeechChunks(fullText);
    return chunks.length > 0 ? chunks : [fullText.trim()];
  }, [fullText]);

  sentencesRef.current = sentences;

  const isSpeaking = speechStatus === 'started';
  const isPaused = speechStatus === 'paused';

  // Stop speech synthesis helper
  const handleStopSpeech = useCallback(() => {
    playbackSessionRef.current += 1;
    isPlayingRef.current = false;
    currentSentenceIdxRef.current = 0;
    lastCharIndexRef.current = 0;
    clearTimers();
    activeUtteranceRef.current = null;
    if (typeof window !== 'undefined') {
      window.__esiActiveUtterance = null;
    }
    setSpeechStatus('stopped');
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      try {
        if (
          window.speechSynthesis.speaking ||
          window.speechSynthesis.pending ||
          window.speechSynthesis.paused
        ) {
          window.speechSynthesis.cancel();
        }
      } catch {}
    }
  }, [clearTimers]);

  // Stop and tell the user why nothing is audible (instead of silently failing)
  const failPlayback = useCallback(() => {
    handleStopSpeech();
    message.warning({
      key: 'esi-article-tts-unavailable',
      duration: 6,
      content:
        t('text_read_aloud_unavailable') ||
        '当前浏览器或设备无法朗读，请尝试使用 Chrome / Edge 浏览器，或在系统中安装中文语音包。',
    });
  }, [handleStopSpeech, t]);

  // Speak a sentence chunk by chunk sequentially with automatic voice fallback and stall recovery
  const speakSentence = useCallback(
    (index, retryCount = 0, forceDefaultVoice = false, charOffset = 0) => {
      if (!isSpeechSupported()) {
        failPlayback();
        return;
      }
      const list = sentencesRef.current;
      const sessionId = playbackSessionRef.current;

      if (index >= list.length || !isPlayingRef.current) {
        clearTimers();
        isPlayingRef.current = false;
        activeUtteranceRef.current = null;
        window.__esiActiveUtterance = null;
        setSpeechStatus('stopped');
        currentSentenceIdxRef.current = 0;
        lastCharIndexRef.current = 0;
        return;
      }

      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (watchdogTimerRef.current) {
        clearTimeout(watchdogTimerRef.current);
        watchdogTimerRef.current = null;
      }
      if (endGuardTimerRef.current) {
        clearTimeout(endGuardTimerRef.current);
        endGuardTimerRef.current = null;
      }
      rearmEndGuardRef.current = null;
      utteranceStartedRef.current = false;

      currentSentenceIdxRef.current = index;
      lastCharIndexRef.current = charOffset;

      const fullSentence = list[index] || '';
      const textToSpeak =
        charOffset > 0 && charOffset < fullSentence.length
          ? fullSentence.slice(charOffset)
          : fullSentence;

      if (!textToSpeak.trim()) {
        speakSentence(index + 1, 0, forceDefaultVoice, 0);
        return;
      }

      const articleLang = speechLangRef.current;
      // Chinese articles often contain English-only lines (brand names, award titles). Many Chinese voices
      // spell those out or garble them, so read English-only chunks with an English voice when one exists.
      const wantsEnglishVoice =
        articleLang !== 'en-US' &&
        !/[\u3400-\u9fff]/.test(textToSpeak) &&
        /[A-Za-z]{2,}/.test(textToSpeak);
      let targetLang = wantsEnglishVoice ? 'en-US' : articleLang;

      // Always query latest voices on-demand in case getVoices() populated after initial render
      let availableVoices = voicesRef.current;
      try {
        const freshVoices = window.speechSynthesis.getVoices();
        if (Array.isArray(freshVoices) && freshVoices.length > 0) {
          availableVoices = freshVoices;
          voicesRef.current = freshVoices;
        }
      } catch {}

      let matchedVoice = !forceDefaultVoice
        ? selectNeutralVoice(availableVoices, targetLang, failedVoicesRef.current)
        : undefined;
      if (!matchedVoice && targetLang !== articleLang && !forceDefaultVoice) {
        // No usable English voice installed: fall back to the article's own (Chinese) voice
        targetLang = articleLang;
        matchedVoice = selectNeutralVoice(availableVoices, targetLang, failedVoicesRef.current);
      }

      // Always create a fresh SpeechSynthesisUtterance instance for each chunk
      let utterance;
      try {
        utterance = new window.SpeechSynthesisUtterance(textToSpeak);
      } catch (err) {
        console.warn('SpeechSynthesisUtterance unavailable:', err);
        failPlayback();
        return;
      }
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;

      if (matchedVoice) {
        try {
          utterance.voice = matchedVoice;
          // Align utterance.lang with the matched voice's language tag to prevent
          // Safari/Android/SAPI silent rejection when a fallback Chinese voice (e.g. zh-CN)
          // is used while the UI is set to zh-HK.
          utterance.lang = String(matchedVoice.lang || targetLang).replace(/_/g, '-');
        } catch {
          utterance.lang = targetLang;
        }
      } else {
        utterance.lang = targetLang;
      }

      // Pin utterance strongly in ref and window to prevent browser GC from dropping onend
      activeUtteranceRef.current = utterance;
      window.__esiActiveUtterance = utterance;

      let hasStarted = false;
      let speakStartedAt = Date.now();
      const isLocalVoice = matchedVoice?.localService === true;
      const markStarted = () => {
        hasStarted = true;
        hasSpokenRef.current = true;
        if (playbackSessionRef.current === sessionId) {
          utteranceStartedRef.current = true;
        }
        if (matchedVoice?.voiceURI) {
          verifiedVoicesRef.current.add(matchedVoice.voiceURI);
        }
        if (watchdogTimerRef.current) {
          clearTimeout(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
      };

      // End guard: some engines (Android system TTS, WebViews, hung network voices) never fire `onend`.
      // Allow a generous time based on text length; if the engine is idle by then, advance to the next
      // sentence. If it is still "speaking" long past the estimate, treat it as hung and move on.
      const isCjkText = /[\u3400-\u9fff]/.test(textToSpeak);
      const expectedMs = textToSpeak.length * (isCjkText ? 400 : 150) + 5000;
      let guardExtensions = 0;
      const advanceFromGuard = () => {
        // New session ID first so a late `onend` / `onerror` from this utterance is ignored (no double-advance)
        playbackSessionRef.current += 1;
        const nextSessionId = playbackSessionRef.current;
        try {
          if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
            window.speechSynthesis.cancel();
          }
        } catch {}
        retryTimerRef.current = setTimeout(() => {
          if (playbackSessionRef.current === nextSessionId && isPlayingRef.current) {
            lastCharIndexRef.current = 0;
            speakSentence(index + 1, 0, forceDefaultVoice, 0);
          }
        }, 60);
      };
      const armEndGuard = (ms) => {
        if (endGuardTimerRef.current) clearTimeout(endGuardTimerRef.current);
        endGuardTimerRef.current = setTimeout(() => {
          if (playbackSessionRef.current !== sessionId || !isPlayingRef.current) return;
          if (window.speechSynthesis.paused) {
            armEndGuard(5000);
            return;
          }
          if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
            advanceFromGuard();
            return;
          }
          guardExtensions += 1;
          if (guardExtensions > 3) {
            advanceFromGuard();
          } else {
            armEndGuard(5000);
          }
        }, ms);
      };
      rearmEndGuardRef.current = () => {
        guardExtensions = 0;
        armEndGuard(expectedMs);
      };

      utterance.onstart = () => {
        markStarted();
        armEndGuard(expectedMs);
      };
      utterance.onboundary = (e) => {
        markStarted();
        if (typeof e?.charIndex === 'number' && e.charIndex >= 0) {
          lastCharIndexRef.current = charOffset + e.charIndex;
        }
      };

      utterance.onend = () => {
        if (playbackSessionRef.current !== sessionId || !isPlayingRef.current) return;

        // Some engines "finish" instantly without producing any audio when the voice is unusable
        // (missing voice data, broken WebView). A real utterance can never end before it started, so an
        // end with no start/boundary event in an implausibly short time is a silent failure: route it
        // through the error path (blacklist voice + retry with another voice, or report to the user).
        const elapsedMs = Date.now() - speakStartedAt;
        if (!hasStarted && elapsedMs < Math.max(200, textToSpeak.length * 30)) {
          utterance.onerror({ error: 'silent-end' });
          return;
        }

        if (watchdogTimerRef.current) {
          clearTimeout(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
        if (endGuardTimerRef.current) {
          clearTimeout(endGuardTimerRef.current);
          endGuardTimerRef.current = null;
        }
        lastCharIndexRef.current = 0;
        speakSentence(index + 1, 0, forceDefaultVoice, 0);
      };

      utterance.onerror = (e) => {
        if (playbackSessionRef.current !== sessionId || !isPlayingRef.current) return;
        if (watchdogTimerRef.current) {
          clearTimeout(watchdogTimerRef.current);
          watchdogTimerRef.current = null;
        }
        if (endGuardTimerRef.current) {
          clearTimeout(endGuardTimerRef.current);
          endGuardTimerRef.current = null;
        }

        const errType = e?.error || '';

        // If the browser's async cancel() bled into the newly queued utterance, retry this sentence
        if (errType === 'canceled' || errType === 'interrupted') {
          if (retryCount < 2) {
            retryTimerRef.current = setTimeout(() => {
              if (playbackSessionRef.current === sessionId && isPlayingRef.current) {
                speakSentence(index, retryCount + 1, forceDefaultVoice, lastCharIndexRef.current);
              }
            }, 60);
          } else {
            failPlayback();
          }
          return;
        }

        // Browser autoplay/permission policy blocked speech: tell the user instead of failing silently
        if (errType === 'not-allowed') {
          failPlayback();
          return;
        }

        // Only blacklist a voice if it has never succeeded on this device
        if (matchedVoice?.voiceURI && !verifiedVoicesRef.current.has(matchedVoice.voiceURI)) {
          failedVoicesRef.current.add(matchedVoice.voiceURI);
        }

        if (retryCount < 3) {
          const nextForceDefault = retryCount >= 2;
          retryTimerRef.current = setTimeout(() => {
            if (playbackSessionRef.current === sessionId && isPlayingRef.current) {
              speakSentence(index, retryCount + 1, nextForceDefault, lastCharIndexRef.current);
            }
          }, 50);
          return;
        }

        // If sentence still fails after fallback retries, skip it. Give up (and tell the user) only if
        // nothing has ever been spoken successfully on this device.
        if (index + 1 < list.length && hasSpokenRef.current) {
          lastCharIndexRef.current = 0;
          speakSentence(index + 1, 0, true, 0);
        } else {
          failPlayback();
        }
      };

      // Watchdog: In Mainland China, Chrome's remote Google voices set speechSynthesis.speaking = true
      // immediately while hanging on blocked network connections without ever firing utterance.onstart.
      // If onstart has not fired in time, blacklist that voice (remote voices only), flush the stuck queue
      // under a new session ID, and retry the sentence with the next available local/default voice.
      // Local OS voices get a longer grace period (Windows speech engines can take several seconds to
      // cold-start on slow PCs) and are never blacklisted just for being slow.
      watchdogTimerRef.current = setTimeout(() => {
        if (
          playbackSessionRef.current === sessionId &&
          isPlayingRef.current &&
          !hasStarted
        ) {
          if (
            !isLocalVoice &&
            matchedVoice?.voiceURI &&
            !verifiedVoicesRef.current.has(matchedVoice.voiceURI)
          ) {
            failedVoicesRef.current.add(matchedVoice.voiceURI);
          }
          // Increment session ID before cancel() so the hung utterance's async 'canceled' error is ignored
          playbackSessionRef.current += 1;
          const nextSessionId = playbackSessionRef.current;
          try {
            window.speechSynthesis.cancel();
          } catch {}
          if (retryCount < 3) {
            retryTimerRef.current = setTimeout(() => {
              if (playbackSessionRef.current === nextSessionId && isPlayingRef.current) {
                speakSentence(index, retryCount + 1, retryCount >= 2, lastCharIndexRef.current);
              }
            }, 60);
          } else {
            failPlayback();
          }
        }
      }, isLocalVoice ? 6000 : 2500);

      try {
        if (window.speechSynthesis.paused) {
          window.speechSynthesis.resume();
        }
        speakStartedAt = Date.now();
        window.speechSynthesis.speak(utterance);
      } catch (err) {
        console.warn('speechSynthesis.speak exception:', err);
        failPlayback();
      }
    },
    [clearTimers, failPlayback]
  );

  // Stop when navigating away to another route
  const currentPath = location.pathname + location.search;
  const prevPathRef = useRef(currentPath);
  useEffect(() => {
    if (prevPathRef.current !== currentPath) {
      prevPathRef.current = currentPath;
      handleStopSpeech();
    }
  }, [currentPath, handleStopSpeech]);

  // Stop when language is switched to avoid language mismatch
  const prevLangRef = useRef(i18n.language);
  useEffect(() => {
    if (prevLangRef.current !== i18n.language) {
      prevLangRef.current = i18n.language;
      handleStopSpeech();
    }
  }, [i18n.language, handleStopSpeech]);

  // Clear any stale speech queue left over from a previous page load / refresh (Chrome can keep
  // `speaking`/`paused` stuck across reloads, which makes every new speak() silently queue forever),
  // and stop speech when the page is hidden/unloaded. Cleanup on unmount.
  useEffect(() => {
    if (isSpeechSupported()) {
      try {
        if (
          window.speechSynthesis.speaking ||
          window.speechSynthesis.pending ||
          window.speechSynthesis.paused
        ) {
          window.speechSynthesis.cancel();
        }
      } catch {}
    }

    const cancelOnPageExit = () => {
      try {
        window.speechSynthesis.cancel();
      } catch {}
    };
    window.addEventListener('pagehide', cancelOnPageExit);
    window.addEventListener('beforeunload', cancelOnPageExit);

    return () => {
      window.removeEventListener('pagehide', cancelOnPageExit);
      window.removeEventListener('beforeunload', cancelOnPageExit);
      handleStopSpeech();
    };
  }, [handleStopSpeech]);

  // Mobile browsers / WeChat silently kill speech when the tab or app goes to the background, leaving the
  // button stuck on "Pause". When the page becomes visible again and the engine is idle, switch to
  // "Continue" so the user can resume from where it stopped.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !isPlayingRef.current || !isSpeechSupported()) {
        return;
      }
      try {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
          playbackSessionRef.current += 1;
          isPlayingRef.current = false;
          clearTimers();
          setSpeechStatus('paused');
        }
      } catch {}
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [clearTimers]);

  const handlePlayOrPause = (e) => {
    e.stopPropagation();

    if (!isSpeechSupported()) {
      failPlayback();
      return;
    }

    // 1. If currently speaking -> PAUSE
    // Use native speechSynthesis.pause() while keeping the current session & utterance alive in memory
    // so native resume continues from the exact syllable, while lastCharIndexRef tracks fallback position.
    if (isSpeaking) {
      isPlayingRef.current = false;
      clearTimers();
      setSpeechStatus('paused');
      try {
        window.speechSynthesis.pause();
      } catch {}
      return;
    }

    // 2. If currently paused -> RESUME
    if (isPaused) {
      clearTimers();
      isPlayingRef.current = true;
      setSpeechStatus('started');
      startKeepAlive();

      const resumeIdx =
        currentSentenceIdxRef.current < sentencesRef.current.length
          ? currentSentenceIdxRef.current
          : 0;
      const resumeCharOffset = lastCharIndexRef.current || 0;

      // If the paused utterance never actually started (e.g. paused while a blocked/remote voice was still
      // hanging) its startup watchdog was cleared by the pause, so a plain resume() could hang forever.
      // Flush the engine and re-speak the sentence instead.
      const engineBusy =
        window.speechSynthesis.speaking ||
        window.speechSynthesis.pending ||
        window.speechSynthesis.paused;
      if (engineBusy && !utteranceStartedRef.current) {
        playbackSessionRef.current += 1;
        const restartSession = playbackSessionRef.current;
        try {
          window.speechSynthesis.cancel();
        } catch {}
        retryTimerRef.current = setTimeout(() => {
          if (isPlayingRef.current && playbackSessionRef.current === restartSession) {
            speakSentence(resumeIdx, 0, false, resumeCharOffset);
          }
        }, 60);
        return;
      }

      // Stage 1: If the browser still holds the paused utterance in its native speech engine,
      // call resume() directly so it continues mid-sentence and triggers its existing onend.
      if (window.speechSynthesis.paused || window.speechSynthesis.speaking) {
        try {
          window.speechSynthesis.resume();
        } catch {}
        // Re-arm the lost-`onend` guard for the resumed utterance (timers were cleared on pause)
        rearmEndGuardRef.current?.();

        // Stage 2 Fallback: If the browser's paused buffer died (e.g. paused >15s in Chrome or on Android)
        // and it remains stuck in paused/non-speaking state after 600ms with no progress, re-speak from lastCharIndexRef.
        const idxAtResume = currentSentenceIdxRef.current;
        const charAtResume = lastCharIndexRef.current;
        const sessionAtResume = playbackSessionRef.current;

        retryTimerRef.current = setTimeout(() => {
          if (
            !isPlayingRef.current ||
            playbackSessionRef.current !== sessionAtResume
          ) {
            return;
          }
          const isStuck =
            window.speechSynthesis.paused ||
            (!window.speechSynthesis.speaking &&
              currentSentenceIdxRef.current === idxAtResume &&
              lastCharIndexRef.current === charAtResume);

          if (isStuck) {
            playbackSessionRef.current += 1;
            const nextSession = playbackSessionRef.current;
            try {
              window.speechSynthesis.cancel();
            } catch {}
            retryTimerRef.current = setTimeout(() => {
              if (isPlayingRef.current && playbackSessionRef.current === nextSession) {
                speakSentence(resumeIdx, 0, false, lastCharIndexRef.current || 0);
              }
            }, 60);
          }
        }, 600);
        return;
      }

      // If the OS dropped the paused utterance completely while paused, immediately speak from resumeCharOffset
      playbackSessionRef.current += 1;
      speakSentence(resumeIdx, 0, false, resumeCharOffset);
      return;
    }

    // 3. Otherwise -> START playback from beginning
    playbackSessionRef.current += 1;
    clearTimers();
    try {
      // Only call cancel() if the speech queue actually has active/pending/paused state,
      // avoiding the Chrome/Safari bug where unnecessary synchronous cancel() aborts the next speak().
      if (
        window.speechSynthesis.speaking ||
        window.speechSynthesis.pending ||
        window.speechSynthesis.paused
      ) {
        window.speechSynthesis.cancel();
      }
    } catch {}

    isPlayingRef.current = true;
    setSpeechStatus('started');
    currentSentenceIdxRef.current = 0;
    lastCharIndexRef.current = 0;
    startKeepAlive();

    // Synchronously speak the first utterance inside the direct user click gesture
    speakSentence(0, 0, false, 0);
  };

  if (!fullText || !isSpeechSupported()) return null;

  return (
    <div className={`inline-flex items-center gap-1 self-start sm:self-center select-none ${className}`}>
      {/* Primary Toggle Button: Start / Pause / Continue Listening */}
      <button
        type="button"
        onClick={handlePlayOrPause}
        className={`h-[32px] inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[4px] bg-[#fdecf1] hover:bg-[#fad8e4] active:scale-95 transition-all cursor-pointer border-0 outline-none select-none flex-shrink-0 ${
          isPaused ? 'border border-[#e50a47]/30' : ''
        }`}
        title={
          isSpeaking
            ? t('text_pause_read_aloud') || '暂停朗读'
            : isPaused
              ? t('text_resume_read_aloud') || '继续朗读'
              : t('text_read_aloud_full') || '朗读全文'
        }
      >
        <img
          src={audioSpeakerIcon}
          alt=""
          aria-hidden="true"
          className={`w-3.5 h-3.5 object-contain transition-transform ${
            isSpeaking ? 'animate-pulse scale-105' : ''
          }`}
        />
        <span
          className={`text-xs font-normal leading-none tracking-normal ${
            isPaused ? 'text-[#e50a47] font-medium' : 'text-[#e50a47]'
          }`}
        >
          {isSpeaking
            ? t('text_pause_read_aloud') || '暂停朗读'
            : isPaused
              ? t('text_resume_read_aloud') || '继续朗读'
              : t('text_read_aloud_full') || '朗读全文'}
        </span>
      </button>
    </div>
  );
}
